import i18n from "@shared/i18n/config";

type LabelDescriptor = {
  label: string;
  manualLabel: string;
};

const WORKFLOW_LABELS: Record<string, LabelDescriptor> = {
  draft: { label: "Draft", manualLabel: "Draft EEG study workflow state" },
  scheduled: {
    label: "Scheduled",
    manualLabel: "Scheduled EEG study workflow state",
  },
  importing: { label: "Importing", manualLabel: "EEG import workflow state" },
  preprocessing: {
    label: "Preprocess",
    manualLabel: "EEG preprocessing workflow state",
  },
  ready_for_technical_review: {
    label: "Tech Ready",
    manualLabel: "Ready for technical review",
  },
  ready_for_clinical_review: {
    label: "Clin Ready",
    manualLabel: "Ready for clinical review",
  },
  in_review: { label: "Review", manualLabel: "Clinical review in progress" },
  report_draft: { label: "Draft Rpt", manualLabel: "Report draft in progress" },
  pending_review: { label: "Peer Rev", manualLabel: "Pending peer review" },
  pending_signoff: { label: "Signoff", manualLabel: "Pending sign-off" },
  completed: { label: "Done", manualLabel: "Completed EEG workflow" },
  cancelled: { label: "Cancelled", manualLabel: "Cancelled EEG workflow" },
  archived: { label: "Archived", manualLabel: "Archived EEG workflow" },
  no_show: { label: "No Show", manualLabel: "Patient no-show workflow state" },
};

const PRIORITY_LABELS: Record<string, LabelDescriptor> = {
  stat: { label: "STAT", manualLabel: "STAT EEG priority" },
  urgent: { label: "Urgent", manualLabel: "Urgent EEG priority" },
  high: { label: "High", manualLabel: "High EEG priority" },
  routine: { label: "Routine", manualLabel: "Routine EEG priority" },
  research: { label: "Research", manualLabel: "Research EEG priority" },
};

const READINESS_LABELS: Record<string, LabelDescriptor> = {
  pending: { label: "Queued", manualLabel: "Queued for import" },
  queued: { label: "Queued", manualLabel: "Queued for import" },
  uploading: { label: "Uploading", manualLabel: "Upload in progress" },
  importing: { label: "Uploading", manualLabel: "Upload in progress" },
  preprocessing: {
    label: "Preprocess",
    manualLabel: "Preprocessing in progress",
  },
  processing: {
    label: "Preprocess",
    manualLabel: "Preprocessing in progress",
  },
  running: { label: "Preprocess", manualLabel: "Preprocessing in progress" },
  complete: { label: "Ready", manualLabel: "Recording ready" },
  completed: { label: "Ready", manualLabel: "Recording ready" },
  failed: { label: "Failed", manualLabel: "Processing failed" },
  error: { label: "Failed", manualLabel: "Processing failed" },
  artifact_missing: { label: "Failed", manualLabel: "Processing failed" },
  stuck: { label: "Stuck", manualLabel: "Processing appears stuck" },
};

const WORKFLOW_CODE_ALIASES: Record<string, string> = {
  peer_rev: "pending_review",
  peer_review: "pending_review",
  pending_peer_review: "pending_review",
  tech_ready: "ready_for_technical_review",
  clin_ready: "ready_for_clinical_review",
  draft_rpt: "report_draft",
  preprocess: "preprocessing",
  signoff: "pending_signoff",
  done: "completed",
};

export function normalizeClinicalLabelCode(value?: string | null): string {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return WORKFLOW_CODE_ALIASES[normalized] || normalized;
}

function fallbackLabel(value?: string | null): string {
  const code = normalizeClinicalLabelCode(value);
  if (!code) return "Unknown";
  return code
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getWorkflowStatusShortLabel(
  codeOrName?: string | null,
): LabelDescriptor {
  const code = normalizeClinicalLabelCode(codeOrName);
  const fallback = WORKFLOW_LABELS[code] || {
    label: fallbackLabel(code),
    manualLabel: fallbackLabel(code),
  };
  return {
    label: i18n.t(`dashboard:workflowShort.${code}`, {
      defaultValue: fallback.label,
    }),
    manualLabel: i18n.t(`dashboard:workflowManual.${code}`, {
      defaultValue: fallback.manualLabel,
    }),
  };
}

export function getPriorityShortLabel(codeOrName?: string | null): LabelDescriptor {
  const code = normalizeClinicalLabelCode(codeOrName || "routine");
  const fallback = PRIORITY_LABELS[code] || {
    label: fallbackLabel(code),
    manualLabel: fallbackLabel(code),
  };
  return {
    label: i18n.t(`dashboard:priorityShort.${code}`, {
      defaultValue: fallback.label,
    }),
    manualLabel: i18n.t(`dashboard:priorityManual.${code}`, {
      defaultValue: fallback.manualLabel,
    }),
  };
}

export function getRecordingReadinessShortLabel(
  processingStatus?: string | null,
): LabelDescriptor {
  const code = normalizeClinicalLabelCode(processingStatus);
  const fallback = READINESS_LABELS[code] || {
    label: "Unknown",
    manualLabel: "Unknown recording readiness",
  };
  return {
    label: i18n.t(`dashboard:readinessShort.${code}`, {
      defaultValue: fallback.label,
    }),
    manualLabel: i18n.t(`dashboard:readinessManual.${code}`, {
      defaultValue: fallback.manualLabel,
    }),
  };
}
